// src/Header.js
import React from 'react';
import { Link } from 'react-router-dom'; // For the title to link to home
import { Calculator, Moon, Sun } from 'lucide-react';

const Header = ({
  darkMode,
  toggleDarkMode,
  currentCurrency,
  handleCurrencyChange, // Expects the new currency code (string)
  currentRegion,
  handleRegionChange,   // Expects the new region code (string)
  currencies,           // The comprehensive CURRENCIES object from App.js
  regions,              // The comprehensive REGIONS object from App.js
  onNavigate            // Optional: if header has other internal links needing dirty check
}) => {
  return (
    <header className="sticky top-0 z-50 bg-white dark:bg-gray-900 shadow-md border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center sm:justify-between sm:h-16 py-3 sm:py-0">
          {/* Logo and Title */}
          <Link
            to="/"
            className="flex items-center space-x-3 mb-3 sm:mb-0 group"
            aria-label="Go to TabDivider homepage"
            onClick={(e) => {
              // If navigating to "/" and current path is not "/", use onNavigate
              if (window.location.pathname !== "/" && onNavigate) {
                e.preventDefault();
                onNavigate("/");
              }
              // Otherwise, allow default Link behavior
            }}
          >
            <Calculator className="h-7 w-7 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-500 transition-colors" />
            <h1 className="text-2xl font-semibold text-gray-800 dark:text-white group-hover:text-gray-900 dark:group-hover:text-gray-50 transition-colors">TabDivider</h1>
          </Link>

          {/* Controls: Selectors and Dark Mode Toggle */}
          <div className="flex items-center flex-wrap justify-center gap-2 sm:gap-3">
            {/* Currency Selector */}
            {currencies && Object.keys(currencies).length > 0 && (
              <div>
                <label htmlFor="global-currency-selector" className="sr-only">Select Currency</label>
                <select
                  id="global-currency-selector"
                  value={currentCurrency}
                  onChange={(e) => handleCurrencyChange(e.target.value)} // Correctly passing the value
                  className="px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm focus:ring-blue-500 focus:border-blue-500 shadow-sm appearance-none"
                  aria-label="Select global application currency"
                >
                  {Object.entries(currencies).map(([code, { symbol, name }]) => (
                    <option key={code} value={code}>
                      {code} ({symbol}) - {name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Region Selector */}
            {regions && Object.keys(regions).length > 0 && (
              <div>
                <label htmlFor="global-region-selector" className="sr-only">Select Region</label>
                <select
                  id="global-region-selector"
                  value={currentRegion}
                  onChange={(e) => handleRegionChange(e.target.value)} // Correctly passing the value
                  className="px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm focus:ring-blue-500 focus:border-blue-500 shadow-sm appearance-none"
                  aria-label="Select global application region"
                >
                  {Object.entries(regions).map(([code, { name }]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900"
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;